import React, { useMemo } from "react";
import { DocumentMetadata } from "@neum/frontend-shared/types/metagraphTypes";
import { NewDocumentButton } from "./multidoctabs/NewDocumentButton";
import { MultiDocPicker } from "./multidoctabs/MultiDocPicker";
import MultiDocSelectorTab from "./multidoctabs/MultiDocSelectorTab";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";

interface MultiDocSelectorProps {
    documentType: string;
    activeDocumentId: string;
    sessionDocumentsMetadata: DocumentMetadata[];
    tabsDisabled?: boolean;
    addDisabledMessage?: string;
    onSelectedDocChange: (documentId: string) => Promise<void>;
    onAddNewNote: () => void;
    onSaveDocumentTitle: (newTitle: string) => Promise<void>;
}

export const MultiDocSelector: React.FC<MultiDocSelectorProps> = ({
    documentType,
    activeDocumentId,
    sessionDocumentsMetadata,
    tabsDisabled,
    addDisabledMessage,
    onSelectedDocChange,
    onAddNewNote,
    onSaveDocumentTitle,
}) => {
    const activeDocument = useMemo(() => {
        return sessionDocumentsMetadata.find(
            (doc) => doc.id === activeDocumentId
        );
    }, [activeDocumentId, sessionDocumentsMetadata]);

    const [leftNavVisible, setLeftNavVisible] = React.useState(false);
    const [rightNavVisible, setRightNavVisible] = React.useState(false);
    const navRef = React.useRef<HTMLElement>(null);

    const handleScrollLeft = () => {
        if (navRef.current) {
            navRef.current.scrollBy({ left: -200, behavior: 'smooth' });
        }
    };

    const handleScrollRight = () => {
        if (navRef.current) {
            navRef.current.scrollBy({ left: 200, behavior: 'smooth' });
        }
    };

    React.useEffect(() => {
        const checkForOverflow = () => {
            if (navRef.current) {
                const nav = navRef.current;
                setLeftNavVisible(nav.scrollLeft > 10);
                setRightNavVisible(nav.scrollWidth > nav.clientWidth && nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 10);
            }
        };

        checkForOverflow();
        // Add resize observer to check for overflow when container size changes
        const resizeObserver = new ResizeObserver(checkForOverflow);
        if (navRef.current) {
            resizeObserver.observe(navRef.current);
        }

        return () => resizeObserver.disconnect();
    }, [sessionDocumentsMetadata]); // Re-run when documents change

    if (sessionDocumentsMetadata.length === 0) return null;

    return (
        <div>
            <div className={`xl:hidden mt-2 mb-2 flex flex-row`}>
                <MultiDocPicker
                    pickerLabel={`All ${documentType.toLowerCase()}s`}
                    activeDocumentId={activeDocumentId}
                    sessionDocumentsMetadata={sessionDocumentsMetadata}
                    tabsDisabled={tabsDisabled}
                    onSelectionChange={onSelectedDocChange}
                />
                <NewDocumentButton
                    documentType={documentType}
                    onAddNewNote={onAddNewNote}
                    addDisabledMessage={addDisabledMessage}
                    tooltipPosition="left"
                    disabled={tabsDisabled}
                />
            </div>
            {/* Desktop (>= xl) screens: We show the compact tabs */}
            <div className="hidden relative xl:block w-full my-3 pt-1 border-t border-b border-gray-300">
                <label
                    htmlFor="docs-tabs"
                    className="absolute -top-2 left-2 inline-block bg-white px-1 text-xs font-medium text-gray-600"
                >
                    {`All ${documentType.toLowerCase()}s`}
                    {(leftNavVisible || rightNavVisible) && ` (${sessionDocumentsMetadata.length.toString()} ${documentType.toLowerCase()}${sessionDocumentsMetadata.length > 1 ? "s" : ""})`}
                </label>
                <div className="border-b border-gray-200 flex flex-row items-center relative">
                    {leftNavVisible && (
                        <div 
                            id="scroll-indicator-left" 
                            className="absolute left-0 top-1/2 transform -translate-y-1/2 bg-gradient-to-r from-white to-transparent w-8 h-full z-2 flex items-center cursor-pointer text-gray-500  hover:text-main-button transition-colors"
                            onClick={handleScrollLeft}
                        >
                            <ChevronLeftIcon className="h-5 w-5 stroke-[3]" />
                        </div>
                    )}
                    <nav
                        ref={navRef}
                        className="flex overflow-x-auto thin-scrollbar-x space-x-2"
                        aria-label="Document tabs"
                        onScroll={(e) => {
                            const nav = e.currentTarget;
                            setLeftNavVisible(nav.scrollLeft > 10);
                            setRightNavVisible(nav.scrollWidth > nav.clientWidth && nav.scrollLeft < nav.scrollWidth - nav.clientWidth - 10);
                        }}
                    >
                        {sessionDocumentsMetadata.map((document) => (
                            <MultiDocSelectorTab 
                                key={document.id}
                                document={document}
                                activeDocument={activeDocument}
                                tabsDisabled={tabsDisabled}
                                onSelectedDocChange={onSelectedDocChange}
                                onSaveDocumentTitle={onSaveDocumentTitle}
                            />
                        ))}
                    </nav>
                    {rightNavVisible && (
                        <div 
                            id="scroll-indicator-right" 
                            className="relative bg-gradient-to-l from-white to-transparent w-8 h-full z-2 flex items-center justify-end cursor-pointer text-gray-500  hover:text-main-button transition-colors"
                            onClick={handleScrollRight}
                        >
                            <ChevronRightIcon className="h-5 w-5 stroke-[3]" />
                        </div>
                    )}
                    <NewDocumentButton
                        documentType={documentType}
                        onAddNewNote={onAddNewNote}
                        addDisabledMessage={addDisabledMessage}
                        disabled={tabsDisabled}
                    />
                </div>
            </div>
        </div>
    );
};
